import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { useCostTypeOptions } from './useCostTypes';
import { calculateTotalCosts, EntityType, EntityWithCosts, getEntityBudget } from './utils';

export type CostFormData = {
    description: string;
    amount: string;
    type: string;
    date_incurred: Date;
};

interface AddCostFormProps {
    entityType: EntityType;
    entity: EntityWithCosts;
    data: CostFormData;
    setData: (field: keyof CostFormData, value: string | number | Date | null | undefined) => void;
    errors: Partial<Record<keyof CostFormData, string>>;
    clearValidationError: (field: keyof CostFormData) => void;
    onSubmit: FormEventHandler<HTMLFormElement>;
    processing: boolean;
    showBudget?: boolean;
}

export default function AddCostForm({
    entityType,
    entity,
    data,
    setData,
    errors,
    clearValidationError,
    onSubmit,
    processing,
    showBudget = true,
}: AddCostFormProps) {
    const { t } = useTranslations();
    const [dateOpen, setDateOpen] = useState(false);
    const costTypeOptions = useCostTypeOptions();

    // Normalize the type value to match options format
    const normalizedType = useMemo(() => {
        if (!data.type) return '';
        // Normalize the type value to match the options format (lowercase, replace spaces with underscores)
        const normalized = data.type.toLowerCase().replace(/\s+/g, '_');
        // Check if normalized value exists in options
        const matchingOption = costTypeOptions.find((opt) => opt.value === normalized);
        return matchingOption ? normalized : data.type;
    }, [data.type, costTypeOptions]);

    // Update type if it needs normalization
    useEffect(() => {
        if (data.type && normalizedType !== data.type) {
            setData('type', normalizedType);
        }
    }, [normalizedType, data.type, setData]);

    const totalCosts = calculateTotalCosts(entity.cost_lines);
    const budget = getEntityBudget(entity);
    const remainingBudget = budget - totalCosts;
    const potentialRemaining = remainingBudget - Number(data.amount || 0);

    // Determine the type label based on entity type - use translations
    const budgetTypeLabel =
        entityType === 'lead' ? t('common.costs.budget.types.estimated_value', 'Estimated Value') : t('common.costs.budget.types.budget', 'Budget');
    const remainingTypeLabel =
        entityType === 'lead' ? t('common.costs.budget.types.value', 'Value') : t('common.costs.budget.types.budget', 'Budget');

    const descriptionError = errors.description;
    const typeError = errors.type;
    const amountError = errors.amount;
    const dateIncurredError = errors.date_incurred;

    return (
        <div>
            {showBudget && budget >= 0 && (
                <div className="bg-muted/50 mb-6 space-y-2 rounded-lg p-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('common.costs.budget.total_budget', `Total ${budgetTypeLabel}:`).replace(':type:', budgetTypeLabel)}
                        </span>
                        <span className="font-medium">{formatCurrency(budget)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('common.costs.budget.current_costs', 'Current Costs:')}</span>
                        <span className="font-medium">{formatCurrency(totalCosts)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-sm">
                        <span className="text-muted-foreground">
                            {t('common.costs.budget.remaining_budget', `Remaining ${remainingTypeLabel}:`).replace(':type:', remainingTypeLabel)}
                        </span>
                        <span className={cn('font-medium', remainingBudget < 0 ? 'text-destructive' : 'text-success-subtle-foreground')}>
                            {formatCurrency(remainingBudget)}
                        </span>
                    </div>
                    {data.amount && (
                        <div className="flex justify-between border-t pt-2 text-sm">
                            <span className="text-muted-foreground">{t('common.costs.budget.after_cost', 'After This Cost:')}</span>
                            <span className={cn('font-medium', potentialRemaining < 0 ? 'text-destructive' : 'text-success-subtle-foreground')}>
                                {formatCurrency(potentialRemaining)}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={onSubmit} className="space-y-6">
                <div className="space-y-2">
                    <Label htmlFor="description">{t('common.costs.labels.description', 'Description')}</Label>
                    <Input
                        id="description"
                        value={data.description}
                        onChange={(e) => {
                            setData('description', e.target.value);
                            clearValidationError('description');
                        }}
                        placeholder={t('common.costs.placeholders.description', 'Describe what this cost is for...')}
                        className={descriptionError ? 'border-destructive' : ''}
                    />
                    {descriptionError && <p className="text-destructive mt-1 text-sm">{descriptionError}</p>}
                </div>

                <div className="w-full space-y-2">
                    <Label htmlFor="type">{t('common.costs.labels.type', 'Type')}</Label>
                    <Combobox
                        options={costTypeOptions}
                        value={normalizedType}
                        onValueChange={(value) => {
                            setData('type', value);
                            clearValidationError('type');
                        }}
                        placeholder={t('common.costs.placeholders.select_type', 'Select cost type')}
                        className={typeError ? 'border-destructive' : ''}
                    />
                    {typeError && <p className="text-destructive mt-1 text-sm">{typeError}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="amount">{t('common.costs.labels.amount', 'Amount (EUR)')}</Label>
                    <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={data.amount}
                        onChange={(e) => {
                            setData('amount', e.target.value);
                            clearValidationError('amount');
                        }}
                        placeholder={t('common.costs.placeholders.amount', '0.00')}
                        className={amountError ? 'border-destructive' : ''}
                    />
                    {amountError && <p className="text-destructive mt-1 text-sm">{amountError}</p>}
                </div>

                <div className="space-y-2">
                    <Label>{t('common.costs.labels.date_incurred', 'Date Incurred')}</Label>
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant={'outline'}
                                className={cn(
                                    'w-full justify-start text-left font-normal',
                                    !data.date_incurred && 'text-muted-foreground',
                                    dateIncurredError && 'border-destructive',
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {data.date_incurred ? formatDate(data.date_incurred) : t('common.costs.placeholders.pick_date', 'Pick a date')}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={data.date_incurred}
                                onSelect={(date) => {
                                    setData('date_incurred', date || new Date());
                                    clearValidationError('date_incurred');
                                    setDateOpen(false);
                                }}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    {dateIncurredError && <p className="text-destructive mt-1 text-sm">{dateIncurredError}</p>}
                </div>

                {potentialRemaining < 0 && data.amount && showBudget && (
                    <div className="bg-danger-subtle rounded-lg border border-transparent p-4">
                        <div className="flex items-center">
                            <div className="text-danger-subtle-foreground text-sm">
                                {t('common.costs.budget.warning', `⚠️ Warning: This cost will exceed the remaining :type by :amount`)
                                    .replace(':type', remainingTypeLabel.toLowerCase())
                                    .replace(':amount', formatCurrency(Math.abs(potentialRemaining)))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-4 pt-4">
                    <Button type="submit" disabled={processing}>
                        {processing ? t('common.costs.buttons.saving', 'Saving...') : t('common.costs.buttons.add_cost', 'Add Cost')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
